import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { queryKeys } from "@/modules/api/queryKeys";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import {
  getUnlockedTutorialsQuery,
  GetUnlockedTutorialsResponse,
} from "@/modules/tutorials/api/queries/unlockedTutorial/getUnlockedTutorialsQuery";
import { ParsedTutorial } from "@/modules/tutorials/types/ParsedTutorial";
import { useUserDataContext } from "@/modules/user/hooks/useUserDataContext";

const translationNamespace = "apiMessages/useGetTutorialsQuery";

export function useGetUnlockedTutorialsQuery() {
  const jsonApi = useJsonApi();

  const { t } = useTranslation(translationNamespace);

  const { currentGroup } = useUserDataContext();

  return useQuery(
    currentGroup
      ? {
          queryKey: [
            queryKeys.unlockedTutorials.getUnlockedTutorialsList,
            currentGroup,
          ],
          queryFn: () =>
            getUnlockedTutorialsQuery(jsonApi, {
              userGroupId: currentGroup.id,
            }),
          retry: false,
          refetchOnWindowFocus: false,
          select: (response) => {
            if (response) {
              return parseUnlockedTutorials(response.data);
            }
          },
          onError: (error) => {
            apiErrorValidate(error, {
              401: {
                UNAUTHORIZED: t(
                  "You are not allowed to view tutorials. Please sign in again"
                ),
              },
              403: {
                ACCESS_FORBIDDEN: t(
                  "You don't have an access to view tutorials"
                ),
              },
              422: {
                INVALID_TUTORIAL_RELATIONS: t(
                  "Tutorials are corrupted. Please contact the administrator"
                ),
              },
            });
          },
        }
      : { enabled: false }
  );
}

function parseUnlockedTutorials(
  fetchedUnlockedTutorials: GetUnlockedTutorialsResponse["data"]
): ParsedTutorial[] {
  return fetchedUnlockedTutorials.map(({ id, attributes, meta }) => ({
    id,
    name: attributes.name,
    minimumLevelOrder: meta.minimumLevelOrder,
    stepsNumber: meta.tutorialStepsNumber,
    stepsCompleted: meta.tutorialStepsCompleted,
    completedAt: meta.completedAt,
    testQuestionsNumber: meta.testQuestionsNumber,
    testPassed: meta.testPassed,
  }));
}
